import { COLLECTIONS } from "./api"

/**
 * Interface for seasonal image configuration
 * Allows defining time-limited special images for collections
 */
export interface SeasonalImage {
  collectionId: string
  seasonalImage: string
  startDate: Date
  endDate: Date
  description?: string
}

/**
 * Array of seasonal images configuration
 * Add new seasonal events here
 */
export const SEASONAL_IMAGES: SeasonalImage[] = [
  {
    collectionId: "ilya",
    seasonalImage: "/images/ilya-halloween-cube.webp",
    startDate: new Date("2025-10-01T00:00:00Z"),
    endDate: new Date("2025-11-01T00:00:00Z"),
    description: "Halloween 2025 special cube for Ilya Kazakov collection",
  },
  {
    collectionId: "ilya",
    seasonalImage: "/images/ilya-xmas2025-cube.webp",
    startDate: new Date("2025-12-21T00:00:00Z"),
    // endDate is exclusive (see getSeasonalImage: now < endDate)
    // To include the full day of Jan 5, we set endDate to Jan 6 00:00:00Z.
    endDate: new Date("2026-01-06T00:00:00Z"),
    description: "Xmas 2025 special cube for Ilya Kazakov collection",
  },
  // Add more seasonal events here in the future
]

/**
 * Get seasonal image for a collection if active
 * Used ONLY when creating new snapshots (minting/editing)
 * 
 * @param collectionId - ID of the collection
 * @returns Seasonal image path if active, null otherwise
 */
export function getSeasonalImage(collectionId: string): string | null {
  const now = new Date()
  
  const activeSeasonal = SEASONAL_IMAGES.find(
    (seasonal) =>
      seasonal.collectionId === collectionId &&
      now >= seasonal.startDate &&
      now < seasonal.endDate
  )
  
  return activeSeasonal ? activeSeasonal.seasonalImage : null
}

/**
 * Get cube image for creating a new collection snapshot
 * Returns seasonal image if active, otherwise returns the default cubeImage from COLLECTIONS
 * 
 * IMPORTANT: Use this function ONLY when creating new snapshots (minting/editing)
 * DO NOT use this when generating images from existing snapshots!
 * 
 * @param collectionId - Collection ID
 * @returns Path to the cube image for snapshot
 */
export function getCubeImageForSnapshot(collectionId: string): string {
  const collection = COLLECTIONS.find(c => c.id === collectionId)
  
  if (!collection) {
    console.warn(`[SeasonalImages] Collection not found: ${collectionId}`)
    return `/images/${collectionId}-cube.webp`
  }
  
  // Check for active seasonal image
  const seasonalImage = getSeasonalImage(collectionId)
  
  // Return seasonal image if active, otherwise return default cubeImage
  return seasonalImage || collection.cubeImage
}

/**
 * Get cube image for UI preview (not for snapshots)
 * This is safe to use in UI components that don't affect minted totems
 * 
 * @param collectionId - Collection ID
 * @returns Path to the cube image for preview
 */
export function getCubeImageForPreview(collectionId: string): string {
  return getCubeImageForSnapshot(collectionId)
}

/**
 * Get block variant based on cubeImage path
 * Determines if a block is seasonal (Halloween, Christmas, etc) or default
 * 
 * @param cubeImage - Path to the cube image
 * @returns Block variant string (e.g., "Halloween", "Default")
 */
export function getBlockVariant(cubeImage: string): string {
  if (!cubeImage || typeof cubeImage !== 'string') {
    return "Default"
  }
  
  // Check for seasonal markers in the path
  if (cubeImage.includes('halloween')) {
    return "Halloween"
  }
  if (cubeImage.includes('christmas') || cubeImage.includes('xmas')) {
    return "Christmas"
  }
  if (cubeImage.includes('easter')) {
    return "Easter"
  }
  if (cubeImage.includes('valentine')) {
    return "Valentine"
  }
  
  // Default for regular images
  return "Default"
}

/**
 * Check if a collection has an active seasonal image
 * @param collectionId - ID of the collection
 * @returns true if seasonal image is active
 */
export function hasActiveSeasonalImage(collectionId: string): boolean {
  return getSeasonalImage(collectionId) !== null
}

